import { domains, link, params as routeParams } from '@aline/routes';
import { z } from 'zod';

import { getReportCellColumnKey } from './types/mention-urls';
import type { ReportCell } from './types/mention-urls';

export enum ValidEnv {
  ci = 'ci',
  dev = 'dev',
  production = 'production',
  qa = 'qa',
  staging = 'staging',
  test = 'test',
}

export function isValidEnv(val: unknown): val is ValidEnv {
  return typeof val === 'string' && Object.values(ValidEnv).some(v => v === val);
}

export function getBaseRoute(env: ValidEnv): string {
  switch (env) {
    case ValidEnv.dev:
    case ValidEnv.ci:
      return domains.dev;
    case ValidEnv.staging:
      return domains.staging;
    case ValidEnv.production:
    default:
      return domains.prod;
  }
}

export function getBaseServerRoute(env: ValidEnv): string {
  switch (env) {
    case ValidEnv.dev:
    case ValidEnv.ci:
      return domains.server;
    case ValidEnv.staging:
      return domains.staging;
    case ValidEnv.production:
    default:
      return domains.prod;
  }
}

/**
 * URL param keys.
 */

export const collaboratorsParamKey = 'editCollaborators';
export const declineParamKey = 'decline';
export const emailVerifiedKey = 'emailVerified';
export const forwardParamKey = 'forwardDocument';
export const navToNextFieldParamKey = 'navToNextField';
export const signupParamKey = 'signup';
export const s4sFlowParamKey = 's4sFlow';
export const redirectPathKey = 'redirectPath';
export const tokenKey = 'token';
export const tourIdKey = 'tourId';
export const templateQuickStartParamKey = 'quickStart';
export const pendingReviewParamKey = 'pendingReview';
export const walkThroughParamKey = 'walkthrough';
export const reminderAutomationIdParamKey = 'automationId';
export const reminderReportIdParamKey = 'reportId';

/**
 * URL-fragment / DOM-id prefix for a single activity-feed row, keyed by ActivityItem id.
 * Shared by the client (row element ids + hash scrolling) and the server (notification link
 * builders), so both sides agree on one format.
 */
export const ACTIVITY_ANCHOR_PREFIX = 'activity:' as const;

/**
 * `guid` rather than `uuid`: some stored ActivityItem ids are UUID-shaped without RFC-compliant
 * version/variant bits, and a strict check drops deep links to those rows. This only needs to
 * reject malformed hashes before the id is used as a query anchor — a well-formed id for a row
 * that does not exist resolves to an empty feed anyway.
 */
const activityItemIdSchema = z.guid();

/** `#activity:<id>` / DOM id shape produced by {@link getActivityAnchorId}. */
export type ActivityAnchorId = `${typeof ACTIVITY_ANCHOR_PREFIX}${string}`;

/**
 * Builds the URL-fragment anchor for a single activity-feed row. Every activity type (comment,
 * status update, AI thread) uses it, so a deep link lands on the exact row regardless of kind.
 */
export function getActivityAnchorId(activityItemId: string): ActivityAnchorId {
  return `${ACTIVITY_ANCHOR_PREFIX}${activityItemId}`;
}

/**
 * Extracts the ActivityItem id from an activity anchor fragment (with or without a leading `#`).
 * Returns null for non-activity hashes (e.g. legacy `#comment:…`) and malformed ids.
 */
export function parseActivityAnchorId(hashOrAnchor: string): string | null {
  const raw = hashOrAnchor.startsWith('#') ? hashOrAnchor.slice(1) : hashOrAnchor;
  if (!raw) return null;

  const decoded = decodeUrlHashFragment(raw);
  if (!decoded?.startsWith(ACTIVITY_ANCHOR_PREFIX)) return null;

  const id = decoded.slice(ACTIVITY_ANCHOR_PREFIX.length);
  return activityItemIdSchema.safeParse(id).success ? id : null;
}

/** Decodes a URL hash fragment; returns null when the fragment is not valid URI encoding. */
export function decodeUrlHashFragment(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

// Type used for setting Aline URL params.
export interface URLParamsOpts {
  aiChatItemParams?: { aiChatItem: string };
  commentParams?: { commentId: string };
  declineParams?: { decline: true };
  forwardParams?: { forward: true };
  questionIdParams?: { questionId: string };
  s4sFlowParams?: { s4sOn: boolean };
  showCollaboratorsDialogParams?: { showCollaboratorsDialog: boolean };
  templateQuickStartParams?: { quickStartOn: boolean };
  tourParams?: { tourId?: number };
  walkThroughParams?: { autoStartWalkthrough?: boolean; navToNextField?: boolean };
}

export type WorkflowTab =
  | 'comments'
  | 'documents'
  | 'options'
  | 'steps'
  | 'summary'
  | 'team'
  | 'tasks';
export interface WorkflowRouteOpts extends Pick<URLParamsOpts, 'commentParams'> {
  isTemplate?: boolean;
  tab?: WorkflowTab;
}

export function getWorkflowRoute(workflowId: string, options: WorkflowRouteOpts = {}): string {
  const { isTemplate, tab = 'summary' } = options;
  const mapping = {
    comments: isTemplate
      ? link.TemplateSelectedTemplateComments({ workflowId })
      : link.WorkflowDetailTabComments({ workflowId }),
    documents: isTemplate
      ? link.TemplateSelectedTemplateDocuments({ workflowId })
      : link.WorkflowDetailTabDocuments({ workflowId }),
    options: isTemplate
      ? link.TemplateSelectedTemplateOptions({ workflowId })
      : link.WorkflowDetailTabOptions({ workflowId }),
    steps: isTemplate
      ? link.TemplateSelectedTemplateSteps({ workflowId })
      : link.WorkflowDetailTabSteps({ workflowId }),
    summary: isTemplate
      ? link.TemplateSelectedTemplateSummary({ workflowId })
      : link.WorkflowDetailTabSummary({ workflowId }),
    tasks: isTemplate
      ? link.TemplateSelectedTemplateTasks({ workflowId })
      : link.WorkflowDetailTabTasks({ workflowId }),
    team: isTemplate
      ? link.TemplateSelectedTemplateTeam({ workflowId })
      : link.WorkflowDetailTabCollaborators({ workflowId }),
  } as const;
  const baseRoute = mapping[tab];

  return appendAlineUrlParams(baseRoute, options);
}

export function getDocumentRoute(documentId: string): string {
  return link.DocumentDetail({ documentId });
}

export function getReportRoute(reportId: string): string {
  return link.ReportDetail({ reportId });
}

/**
 * Deep link to one report cell: the report route, plus the params `Report.tsx`
 * reads back to scroll to a row and highlight a column.
 *
 * Shared rather than interpolated at each call site because the ids are not
 * guaranteed to be URL-safe. An id containing `&`, `=`, or `#` interpolated
 * straight into the query string would split into extra params or a fragment,
 * and the reader would scroll to the wrong row or none at all.
 * `URLSearchParams` encodes them; the path needs nothing, because chicane
 * `encodeURIComponent`s each route param.
 */
export function buildReportCellHref({ column, documentId, reportId }: ReportCell): string {
  const searchParams = new URLSearchParams({
    highlightCol: getReportCellColumnKey(column),
    scrollToDoc: documentId,
  });
  return `${getReportRoute(reportId)}?${searchParams.toString()}`;
}

export function getEditorWorkflowRoute(
  documentId: string,
  workflowId: string,
  options: {
    commentId?: string;
    decline?: true;
    forward?: true;
    s4sFlow?: boolean;
    templateQuickStart?: boolean;
    walkThroughParams?: URLParamsOpts['walkThroughParams'];
  } = {}
): string {
  const {
    commentId,
    decline,
    forward,
    s4sFlow = false,
    templateQuickStart,
    walkThroughParams,
  } = options;
  const baseRoute = link.DocumentDetailWorkflowDetail({ documentId, workflowId });
  const forwardParams = forward
    ? ({ forwardParams: { forward } } satisfies Pick<URLParamsOpts, 'forwardParams'>)
    : {};
  const declineParams = decline
    ? ({ declineParams: { decline } } satisfies Pick<URLParamsOpts, 'declineParams'>)
    : {};
  const commentParams = commentId
    ? ({ commentParams: { commentId } } satisfies Pick<URLParamsOpts, 'commentParams'>)
    : {};
  const s4sFlowParams = s4sFlow
    ? ({ s4sFlowParams: { s4sOn: true } } satisfies Pick<URLParamsOpts, 's4sFlowParams'>)
    : {};
  const quickStartParams = templateQuickStart
    ? ({
        templateQuickStartParams: { quickStartOn: true },
      } satisfies Pick<URLParamsOpts, 'templateQuickStartParams'>)
    : {};
  return appendAlineUrlParams(baseRoute, {
    ...forwardParams,
    ...declineParams,
    ...commentParams,
    ...s4sFlowParams,
    ...quickStartParams,
    walkThroughParams,
  });
}

export function getWorkflowDoneSigningRoute(workflowId: string): string {
  return link.WorkflowDoneSigning({ workflowId });
}

/**
 * Functions to create links that can be used externally.
 */
export function docLinkRoute(docId: string, env: ValidEnv): string {
  const base = getBaseRoute(env);
  const docRoute = getDocumentRoute(docId);

  return `${base}${docRoute}`;
}

// Helper function for building a link that gets emailed to users when sharing a Document.  Returns the *relative* link.
export function createDocumentRoute(
  documentId: string,
  options: {
    aiChatItem?: string;
    autoStartWalkthrough?: boolean;
    commentId?: string;
    forward?: boolean;
    questionId?: string;
    showCollaboratorsDialog?: boolean;
    tourId?: number;
  } = {}
): string {
  const {
    aiChatItem,
    autoStartWalkthrough = false,
    commentId,
    forward,
    questionId,
    showCollaboratorsDialog,
    tourId,
  } = options;
  const baseRoute = getDocumentRoute(documentId);
  const commentParams = commentId
    ? ({ commentParams: { commentId } } satisfies Pick<URLParamsOpts, 'commentParams'>)
    : {};
  const questionIdParams = questionId
    ? ({ questionIdParams: { questionId } } satisfies Pick<URLParamsOpts, 'questionIdParams'>)
    : {};
  const forwardParams = forward
    ? ({ forwardParams: { forward } } satisfies Pick<URLParamsOpts, 'forwardParams'>)
    : {};
  const editCollaboratorsParams = showCollaboratorsDialog
    ? ({
        showCollaboratorsDialogParams: { showCollaboratorsDialog },
      } satisfies Pick<URLParamsOpts, 'showCollaboratorsDialogParams'>)
    : {};
  const tourParams = tourId
    ? ({ tourParams: { tourId } } satisfies Pick<URLParamsOpts, 'tourParams'>)
    : {};
  const aiChatItemParams = aiChatItem
    ? ({ aiChatItemParams: { aiChatItem } } satisfies Pick<URLParamsOpts, 'aiChatItemParams'>)
    : {};
  return appendAlineUrlParams(baseRoute, {
    ...commentParams,
    ...forwardParams,
    ...editCollaboratorsParams,
    ...tourParams,
    ...aiChatItemParams,
    ...questionIdParams,
    walkThroughParams: { autoStartWalkthrough },
  });
}

/**
 * Appends various params to an Aline route.
 */
export function appendAlineUrlParams(route: string, options: URLParamsOpts): string {
  const params = new URLSearchParams();
  const {
    aiChatItemParams,
    commentParams,
    declineParams,
    forwardParams,
    questionIdParams,
    s4sFlowParams,
    showCollaboratorsDialogParams,
    templateQuickStartParams,
    tourParams,
    walkThroughParams,
  } = options;

  if (walkThroughParams) {
    if (walkThroughParams.autoStartWalkthrough)
      params.append(walkThroughParamKey, `${walkThroughParams.autoStartWalkthrough}`);
    if (walkThroughParams.navToNextField)
      params.append(navToNextFieldParamKey, `${walkThroughParams.navToNextField}`);
  }

  if (showCollaboratorsDialogParams?.showCollaboratorsDialog)
    params.append(
      collaboratorsParamKey,
      `${showCollaboratorsDialogParams.showCollaboratorsDialog}`
    );
  if (forwardParams) params.append(forwardParamKey, `${forwardParams.forward}`);
  if (declineParams) params.append(declineParamKey, `${declineParams.decline}`);
  if (commentParams) params.append(routeParams.commentId, commentParams.commentId);
  if (s4sFlowParams) params.append(s4sFlowParamKey, `${s4sFlowParams.s4sOn}`);
  if (templateQuickStartParams)
    params.append(templateQuickStartParamKey, `${templateQuickStartParams.quickStartOn}`);
  if (tourParams) params.append(tourIdKey, `${tourParams.tourId}`);
  if (questionIdParams) params.append(routeParams.questionId, `${questionIdParams.questionId}`);
  if (aiChatItemParams) params.append(routeParams.aiChatItem, aiChatItemParams.aiChatItem);

  // If params string is empty, don't pollute the route with an empty ?.
  const paramsString = params.toString();
  if (!paramsString) return route;

  return `${route}?${paramsString}`;
}
